import { getDateRange } from '@modules/common/components/DateRangePicker/helpers';
import { EqlFieldName } from '@modules/common/constants';
import { Category } from '@modules/common/types';
import {
  getFieldNameFromEqlField,
  getMetadataQBFieldFromEqlField,
} from '@modules/common/utils';
import { getHasMetadataFilterForEqlField } from '@modules/quantify/utils';
import { TimeUnit } from 'generated/graphql';
import { isEqual } from 'lodash';
import { TimeRange } from 'v2/core/entities';
import { QueryContainer } from 'v2/core/services/notebook/types';
import { combineWhereClauses } from 'v2/core/services/queryBuilder/combineWhereClauses';
import { decodeSourceFromString } from 'v2/core/services/queryBuilder/utils';
import {
  EmptyWhereClause,
  OperationType,
  WhereClause,
} from 'v2/core/services/queryBuilder/whereClause';
import {
  categoryWhereClauseGenerator,
  getWhereClauseForFieldName,
} from 'v2/core/services/queryBuilder/whereClauseGenerator';
import { QuantifyState } from '../store/types';

interface getQuantifyQueryContainerOptions {
  query: QuantifyState['query'];
  metricKeyId: string;
  metricCategory: Category | undefined;
  whereClause: WhereClause;
  dateRange: TimeRange;
}

export function getQuantifyQueryContainer({
  query,
  metricKeyId,
  metricCategory,
  whereClause,
  dateRange,
}: getQuantifyQueryContainerOptions): QueryContainer | null {
  const measuredAsCriteriaWhereClause = getHasMetadataFilterForEqlField(
    query.formulaQuery?.criteria,
  );

  if (!metricKeyId) {
    const groupByWhereClause = getHasMetadataFilterForEqlField(
      query.groupByField,
    );

    const unionOfGroupByAndBaseWhereClause = groupByWhereClause
      ? combineWhereClauses(whereClause, groupByWhereClause, OperationType.AND)
      : whereClause;

    return {
      AggregatePeriod: TimeUnit.Week,
      DateRange: dateRange,
      WhereClause: measuredAsCriteriaWhereClause
        ? combineWhereClauses(
            measuredAsCriteriaWhereClause,
            unionOfGroupByAndBaseWhereClause,
            OperationType.AND,
          )
        : unionOfGroupByAndBaseWhereClause,
    };
  }

  const dataPointClause = getWhereClauseForFieldName({
    fieldName: query.groupByField
      ? getFieldNameFromEqlField(query.groupByField)
      : null,
    values:
      query.groupByField?.field === EqlFieldName.Source && metricKeyId
        ? [decodeSourceFromString(metricKeyId)]
        : [metricKeyId],
    op: OperationType.AND,
    qbField: query.groupByField
      ? getMetadataQBFieldFromEqlField(query.groupByField)
      : '',
  });

  const dataPointClauseWithCategory =
    metricCategory && query.groupByField?.field === EqlFieldName.Entity
      ? combineWhereClauses(
          dataPointClause,
          categoryWhereClauseGenerator([metricCategory]),
          OperationType.AND,
        )
      : dataPointClause;

  const WhereClause = isEqual(EmptyWhereClause, whereClause)
    ? dataPointClauseWithCategory
    : combineWhereClauses(
        whereClause,
        dataPointClauseWithCategory,
        OperationType.AND,
      );

  const unionOfWhereClauseAndMeasuredAsCriteria = measuredAsCriteriaWhereClause
    ? combineWhereClauses(
        measuredAsCriteriaWhereClause,
        WhereClause,
        OperationType.AND,
      )
    : WhereClause;

  const getUpdatedDateRange = () => {
    if (dateRange?.type !== 'absolute') {
      return dateRange;
    }

    const updatedDateRange = {
      ...dateRange,
    };

    const queryDurationDateRange = getDateRange(dateRange);

    if (
      queryDurationDateRange[0] &&
      updatedDateRange.startAfter.isBefore(queryDurationDateRange[0])
    ) {
      updatedDateRange.startAfter = queryDurationDateRange[0];
    }

    if (
      queryDurationDateRange[1] &&
      updatedDateRange.endBefore.isAfter(queryDurationDateRange[1])
    ) {
      updatedDateRange.endBefore = queryDurationDateRange[1];
    }

    return updatedDateRange;
  };

  return {
    AggregatePeriod: TimeUnit.Week,
    DateRange: getUpdatedDateRange() as TimeRange,
    WhereClause: unionOfWhereClauseAndMeasuredAsCriteria,
  };
}
